import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
  useState 
} from "react";
import {
  Autoplay,
  Mousewheel,
  Scrollbar,
  type Swiper as SwiperType
} from "swiper";
import { SwiperSlide } from "swiper/react";
import { StyledCarousel } from "./Carousel.styles";
import { PaginationCarousel } from "../../components/PaginationCarousel/PaginationCarousel.component";
import { useSweepState } from "../../hooks";

type IProps<T extends { title: string }> = {
  carouselData: T[];
  renderElement: (element: T, index?: number) => JSX.Element;
  slidesPerView?: number | "auto";
  centeredSlides?: boolean;
  spaceBetween?: number;
  isBoxShadowEnabled?: boolean;
  loopSlides?: boolean;
  hasPagination: boolean;
  hasPaginationSweeperBullets: boolean;
  hideSweeperBulletsRight?: boolean;
  title: string;
  renderElementBigcarouselGridView?: (element: T) => JSX.Element;
  pauseOnMouseEnter?: boolean;
  autoplay?:boolean
};

// eslint-disable-next-line no-empty-pattern
export const Carousel = <T extends { title: string },>({
  carouselData,
  renderElement,
  slidesPerView = "auto",
  hasPagination,
  hasPaginationSweeperBullets,
  title,
  centeredSlides = false,
  loopSlides = false,
  spaceBetween = 0,
  hideSweeperBulletsRight = false,
  pauseOnMouseEnter = false,
  renderElementBigcarouselGridView,
  autoplay=true
}: IProps<T>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    setSwiperInstance,
    swiperInstance,
    autoSweep,
    handleAutoSweep
  } = useSweepState();

  const [isCarouselView, setIsCarouselView] = useState(true);
  const [carouselIndexStatus, setCarouselIndexStatus] = useState({
    index: 0,
    isBeginning: true,
    isEnd: false
  });
  
  const onSetActiveSlide = (state: SwiperType) => {
    setCarouselIndexStatus({
      index: state.activeIndex,
      isBeginning: state.isBeginning,
      isEnd: state.isEnd
    });
    
    setSwiperInstance(state);
  };
  const handleGridViewSwitch = () => {
    setIsCarouselView(prev => !prev);
  };

  return (
    <>
      <Box>
        <PaginationCarousel
          idScope={ title }
          autoSweep={ autoSweep }
          handleAutoSweep={ handleAutoSweep }
          swiperInstance={ swiperInstance }
          activeIndex={ carouselIndexStatus.index || 0 }
          isBeginning={ carouselIndexStatus.isBeginning }
          isEnd={ carouselIndexStatus.isEnd }
          handleGridViewSwitch={ handleGridViewSwitch }
          isCarouselView={ isCarouselView }
          hasPagination={ hasPagination }
          hasPaginationSweeperBullets={ hasPaginationSweeperBullets }
          hasGreedView={ true }
          hideSweeperBulletsRight={ hideSweeperBulletsRight }
          items={ carouselData }
        />
      </Box>
      { isCarouselView ? (
        <Box>
          <StyledCarousel
            onResize={ onSetActiveSlide }
            role="region"
            aria-roledescription="carousel"
            aria-label={ title === "bigcarousel" ? "Bigbanner Carousel" : title }
            autoplay={
              autoplay
                ? {
                  delay: 7000,
                  disableOnInteraction: true,
                  pauseOnMouseEnter: pauseOnMouseEnter
                }
                : false
            }
            slidesPerView={ slidesPerView }
            loop={ loopSlides }
            centeredSlides={ centeredSlides }
            spaceBetween={ spaceBetween }
            direction="horizontal"
            mousewheel={ {
              forceToAxis: true,
              releaseOnEdges: true
            } }
            scrollbar={ { draggable: true } }
            modules={ [Autoplay, Mousewheel, Scrollbar ] }
            simulateTouch={ true }
            onSwiper={ (swiper) =>{
              setSwiperInstance(swiper);
              onSetActiveSlide(swiper);
            } }
            onUpdate={ (swiper) => onSetActiveSlide(swiper) } 
            onSlideChange={ onSetActiveSlide }
            onTransitionEnd={ onSetActiveSlide }
            onActiveIndexChange={ onSetActiveSlide }
          >
            { carouselData.map((element, i: number) => {
              return (
                <SwiperSlide
                  style={ isMobile ?{ width: "100%", boxSizing: "border-box" }:{  } }
                  key={ "carousel-slide-" + i }
                  tabIndex={ -1 }
                >
                  { renderElement(element) }
                </SwiperSlide>
              );
            }) }
          </StyledCarousel>
        </Box>
      ) : (
        <div style={ { display: "flex", flexWrap: "wrap", flexGrow: 1, gap: "0.7rem" } }>
          { carouselData.map((element, i: number) => {
            return (
              <div
                // className="basic-grid-card"
                style={ isMobile ? { width:"24.5rem", boxSizing: "content-box" }:{  boxSizing: "content-box" } }
                key={ "basic-card-" + i }
                role="region"
                aria-label={  !isCarouselView ? element.title : undefined }
              >
                { renderElementBigcarouselGridView ? renderElementBigcarouselGridView(element) :renderElement(element) }
              </div>
            );
          }) }
        </div>
      ) }
    </>
  );
};
